import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  message,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import BusquedaAvanzadaCuentaBancaria from 'components/BusquedaAvanzadaCuentaBancaria';
import InputSearch from 'components/InputSearch';
import { onError } from 'utils/handlers';
import createNewClave from 'utils/createClave';
import { getMetodosDePago, getTiposDePagos } from 'api/configuracion';
import { deleteTipoDePago, patchTipoDePago, postTipoDePago } from 'api/configuracion/tiposDePagos';
import { getCuentaDeBanco, getCuentasDeBanco } from 'api/configuracion/cuentasDeBancos';

// eslint-disable-next-line react/prop-types
const TiposDePagos = ({ permission }) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [dataExterno, setDataExterno] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [metodosDePago, setMetodosDePago] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDePagos();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _metodosDePago,
        _data,
      ] = await Promise.all([getMetodosDePago(), getTiposDePagos()]);
      if (mounted) {
        setData(_data);
        setMetodosDePago(_metodosDePago);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setCuentasBancarias([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchTipoDePago(key, values) : postTipoDePago(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDePago(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    createNewClave(data, form);
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (match.cuenta_banco) {
      const cuentaDeBanco = await getCuentaDeBanco(match.cuenta_banco);
      if (cuentaDeBanco) {
        cuentaDeBanco.metodo_de_pago = cuentaDeBanco.metodo_de_pago?.concepto
          || cuentaDeBanco.metodo_de_pago;
        setCuentasBancarias([cuentaDeBanco]);
      }
    }
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 350,
    },
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 200,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => (estadosGlobales.find((item) => item.id === val)?.descripcion),
    },
  ];

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields(['numero_de_cuenta']);
      const params = form.getFieldsValue(['numero_de_cuenta']);
      if (params.numero_de_cuenta) {
        const results = await getCuentasDeBanco({
          params,
        });
        if (results.length) {
          if (results.length === 1) {
            const [selected] = results;
            form.setFieldsValue({
              cuenta_banco: selected.id,
            });
            selected.metodo_de_pago = selected.metodo_de_pago?.concepto || selected.metodo_de_pago;
            setCuentasBancarias([selected]);
            setVisibleBusqueda(false);
          } else if (results.length) {
            setDataExterno(results);
            setVisibleBusqueda(true);
          } else {
            message.info('No se encontró Cuenta Bancaria');
          }
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  let timeoutBusqueda;
  const onSearch = () => {
    clearTimeout(timeoutBusqueda);
    timeoutBusqueda = setTimeout(handleSearch, 2000);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            mobileColIndex={0}
            permission={permission}
            hasAllViewPermission
          />
        ) : (
          <Col span={24} style={{ height: '100%' }}>
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Pago`}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onCancel={onCancel}
                  loading={loading}
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Form
                layout="vertical"
                form={form}
                onFinish={() => onFinish()}
                scrollToFirstError
                initialValues={{
                  estados_globales: 1,
                  predeterminado: true,
                  pedir_datos_de_operacion: true,
                  ingreso_a_cuenta: true,
                  utilizar_terminales: true,
                }}
              >
                <Row gutter={10}>
                  {/* <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="clave"
                      label="Clave"
                      required
                      disabled={!!selectedRowKeys.length}
                    />
                  </Col> */}
                  {!cuentasBancarias.length ? (
                    <Col xs={24} sm={24} md={16}>
                      <InputSearch
                        onChange={onSearch}
                        label="Cuenta Bancaria"
                        name="numero_de_cuenta"
                        onPressEnter={() => {
                          clearTimeout(timeoutBusqueda);
                          handleSearch();
                        }}
                        onClickAdvanced={() => {
                          setVisibleBusqueda(true);
                        }}
                        title="Búsqueda por número de cuenta"
                      />
                    </Col>
                  ) : (
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="cuenta_banco"
                        label="Cuenta bancaria"
                      >
                        <Select
                          dataSource={cuentasBancarias}
                          onChange={(val) => {
                            if (!val) {
                              setCuentasBancarias([]);
                              form.resetFields(['numero_de_cuenta']);
                            }
                          }}
                          render={(e) => `${e.metodo_de_pago} - ${e.numero_de_cuenta}`}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="cuenta_ingreso"
                      label="Cuenta ingreso"
                      required
                    />
                  </Col>
                  <Col xs={24} sm={24} md={16}>
                    <Form.Item
                      name="descripcion"
                      rules={rules.required}
                      label="Descripción"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="metodo_de_pago"
                      label="Método de pago"
                      rules={rules.required}
                    >
                      <Select dataSource={metodosDePago} labelProp="concepto" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="requisitos"
                      label="Requisitos"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="concepto"
                      label="Concepto"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="predeterminado"
                      label="Predeterminado"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="pedir_datos_de_operacion"
                      label="Pedir datos de operación"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="ingreso_a_cuenta"
                      label="Ingreso a cuenta"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="utilizar_terminales"
                      label="Utilizar terminales"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select trueFalse />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estado"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select disabled={!selectedRowKeys.length} dataSource={estadosGlobales} />
                    </Form.Item>
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </Card>
          </Col>
        )}
        <BusquedaAvanzadaCuentaBancaria
          visible={visibleBusqueda}
          setVisible={setVisibleBusqueda}
          callback={(selected) => {
            setCuentasBancarias([selected]);
            form.setFieldsValue({ cuenta_banco: selected.id });
          }}
          dataExterno={dataExterno}
          setDataExterno={setDataExterno}
          permission={permission}
        />
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={`Tipo de Pago ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDePagos;
